import { Card, Space, Progress, Button, message, Tag, Modal, Spin } from "antd";
import React, { useContext, useEffect, useState, useCallback } from "react";
import { Uploader } from "@fastpix/resumable-uploads";
import { Context } from "../../context";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { getThumbnailUrl } from "../../utils/thumbnailUtils";
import { delay, fetchEpisodeContent, fetchMovieContent, fetchSongContent } from "./utilis";

const { confirm } = Modal;

const MainContentUpload = ({ selectedContent, updateContentChild }) => {
    const [contentState, setContentState] = useState(null);
    const [mainContentExists, setMainContentExists] = useState("processing");
    const [mainContentImage, setMainContentImage] = useState();
    const [mainContentUploading, setMainContentUploading] = useState(false);
    const [mainContentUploadProgress, setMainContentUploadProgress] = useState(0);
    const [currentMainUploader, setCurrentMainUploader] = useState(null);
    const [loading, setLoading] = useState();

    const { token, apiUrl } = useContext(Context);


    useEffect(() => {
        if (JSON.stringify(contentState) !== JSON.stringify(selectedContent)) {
            setContentState(selectedContent);
        }
        setLoading(false);
    }, [contentState, selectedContent]);

    // Determine content state & set thumbnail
    useEffect(() => {
        if (!contentState) return;
        const isDrmContentReady = contentState?.playbackStatusFp?.drm ?? null;
        const isPrivateContentReady = contentState?.playbackStatusFp?.private ?? null;

        let mainContentStatus = null;
        if (isDrmContentReady === "processing" || isPrivateContentReady === "processing") {
            mainContentStatus = "processing";
        } else if (isDrmContentReady === "ready" && isPrivateContentReady === "ready") {
            mainContentStatus = "ready";
        }

        const img = getThumbnailUrl(contentState, "trailerImage");
        setMainContentImage(img);
        setMainContentExists(mainContentStatus);
    }, [contentState]);

    // Get presigned URL from backend
    const getPresignedUrl = async (params) => {
        try {
            const response = await fetch(`${apiUrl}/fpvideo/getPresignedUrl`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(params),
            });

            const data = await response.json();

            if (data.success) {
                return data.data.url;
            } else {
                message.error(data.error || "Failed to get presigned URL");
            }
        } catch (error) {
            message.error("Failed to get upload URL");
        }
    };

    const refreshContent = async () => {
        await delay(2000);

        let updatedContent;
        if (contentState.contentType === "episode") {
            updatedContent = await fetchEpisodeContent({
                apiUrl,
                token,
                seriesId: contentState.seriesId,
                season: contentState.season,
                episode: contentState.number,
            });

        } else if (contentState.contentType === "movie") {
            updatedContent = await fetchMovieContent({
                apiUrl,
                token,
                contentId: contentState.contentId,
            });

        } else {
            updatedContent = await fetchSongContent({
                apiUrl,
                token,
                contentId: contentState.contentId,
            });
        }

        if (updatedContent) {
            updateContentChild(updatedContent);
            setLoading(false);
        }
    };

    // Upload main content
    const handleMainContentUpload = async (file) => {
        if (!selectedContent) {
            message.error("Please select content first");
            return;
        }

        try {
            setMainContentUploading(true);
            setMainContentUploadProgress(0);

            const mainContentParams = {
                content_id: selectedContent.key ?? "", // empty for episode content
                content_type: selectedContent.contentType,
                accessibility: "drm",
                series_id: selectedContent.seriesId || null,
                season: selectedContent.seasonNumber || null,
                episode: selectedContent.episodeNumber || null,
            };

            const signedUrl = await getPresignedUrl(mainContentParams);

            const fileUploader = Uploader.init({
                endpoint: signedUrl,
                file: file,
                chunkSize: 153600,
                retryAttempts: 5,
            });

            setCurrentMainUploader(fileUploader);

            fileUploader.on("progress", (event) => {
                const percentage = Math.round(event.detail.progress);
                setMainContentUploadProgress(percentage);
            });

            fileUploader.on("success", () => {
                setMainContentUploadProgress(100);
                setMainContentUploading(false);
                setCurrentMainUploader(null);
                message.success("Main content uploaded successfully!");
                setLoading(true);
                refreshContent();
                setMainContentExists("processing");
            });

            fileUploader.on("error", (event) => {
                setMainContentUploading(false);
                setCurrentMainUploader(null);
                message.error(
                    "Main content upload failed: " +
                    (event?.detail?.message || "Unknown error")
                );
            });
        } catch (error) {
            setMainContentUploading(false);
            setCurrentMainUploader(null);
            message.error("Failed to start main content upload");
        }
    };

    // Delete main content
    const handleDeleteContent = () => {
        if (!selectedContent) {
            message.error("Please select content first");
            return;
        }

        confirm({
            title: "Are you sure you want to delete this content?",
            content:
                "This action will permanently delete the content and cannot be undone.",
            icon: <ExclamationCircleFilled />,
            okText: "Yes, Delete",
            okType: "danger",
            cancelText: "Cancel",
            onOk: async () => {
                try {

                    const params = new URLSearchParams({
                        content_id: selectedContent.key,
                        content_type: selectedContent.contentType,
                        media_id_fp: selectedContent.mediaIdFp
                    });

                    // For episode video delection
                    if (selectedContent.contentType === "episode") {
                        params.append("series_id", selectedContent.seriesId);
                        params.append("season", selectedContent.seasonNumber);
                        params.append("episode", selectedContent.episodeNumber);
                    }

                    const response = await fetch(
                        `${apiUrl}/fpvideo/delete_content?${params.toString()}`,
                        {
                            method: "DELETE",
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${token}`,
                            },
                        }
                    );

                    const result = await response.json();

                    if (result.success) {
                        message.success("Content deleted successfully!");
                        setLoading(true);
                        refreshContent();
                    } else {
                        message.error(result.message || "Failed to delete content");
                    }
                } catch (error) {
                    message.error("Failed to delete content");
                }
            },
        });
    };

    // Upload controls
    const pauseMainContentUpload = useCallback(() => {
        if (currentMainUploader) {
            currentMainUploader.pause();
            message.info("Main Content Upload paused");
        }
    }, [currentMainUploader]);

    const resumeMainContentUpload = useCallback(() => {
        if (currentMainUploader) {
            currentMainUploader.resume();
            message.info("Main Content Upload resumed");
        }
    }, [currentMainUploader]);

    const abortMainContentUpload = useCallback(() => {
        if (currentMainUploader) {
            currentMainUploader.abort();
            setMainContentUploading(false);
            setMainContentUploadProgress(0);
            setCurrentMainUploader(null);
            message.info("Main Content Upload aborted");
        }
    }, [currentMainUploader]);

    return (
        <Card title="Main Content (MP4 only)" size="default" style={{ width: 500 }}>
            {
                loading === true ? (
                    <div style={{ textAlign: "center", padding: "20px" }}>
                        <Spin tip="Loading Content Updates" />
                    </div>
                ) : (
                    <Space direction="horizontal" size={30} style={{ width: "100%" }}>
                        <img
                            src={mainContentImage}
                            alt="mainContentImage"
                            style={{ maxWidth: 360, maxHeight: 120 }}
                        />

                        <Space direction="vertical" style={{ width: "100%" }}>
                            {mainContentExists === "ready" && (
                                <>
                                    <Tag
                                        color="green"
                                        style={{
                                            width: 160,
                                            fontSize: 16,
                                            marginTop: 20,
                                            textAlign: "center",
                                            padding: "5px",
                                        }}
                                    >
                                        Content Exists
                                    </Tag>

                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={mainContentUploading}
                                        disabled={mainContentUploading}
                                        onClick={handleDeleteContent}
                                    >
                                        Delete Content
                                    </Button>
                                </>
                            )}

                            {mainContentExists === "processing" && (
                                <Tag
                                    color="green"
                                    style={{
                                        width: 160,
                                        fontSize: 16,
                                        marginTop: 20,
                                        marginBottom: 20,
                                        textAlign: "center",
                                        padding: "5px",
                                    }}
                                >
                                    Content Processing
                                </Tag>
                            )}

                            {mainContentExists === "failed" && (
                                <Tag
                                    color="red"
                                    style={{
                                        width: 140,
                                        fontSize: 16,
                                        marginTop: 20,
                                        marginBottom: 20,
                                        textAlign: "center",
                                    }}
                                >
                                    Content Failed, Try Re-uploading
                                </Tag>
                            )}

                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={mainContentUploading}
                                disabled={mainContentUploading}
                                onClick={() =>
                                    document.getElementById("mainContentUpload").click()
                                }
                            >
                                {["ready", "processing"].includes(mainContentExists)
                                    ? "Re-Upload Content"
                                    : "Upload Content"}
                            </Button>

                            <input
                                type="file"
                                id="mainContentUpload"
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        handleMainContentUpload(file);
                                    }
                                }}
                                accept="video/mp4"
                                disabled={mainContentUploading}
                                style={{ display: "none" }}
                            />

                            {mainContentUploading && (
                                <Space direction="vertical" style={{ width: "100%" }}>
                                    <Progress percent={mainContentUploadProgress} />
                                    <Space>
                                        <Button size="small" onClick={pauseMainContentUpload}>
                                            Pause
                                        </Button>
                                        <Button size="small" onClick={resumeMainContentUpload}>
                                            Resume
                                        </Button>
                                        <Button size="small" danger onClick={abortMainContentUpload}>
                                            Abort
                                        </Button>
                                    </Space>
                                </Space>
                            )}
                        </Space>
                    </Space>
                )
            }

        </Card>
    );
};

export default React.memo(MainContentUpload);
